// src/storeRotas/index.js - the admin app's Store rotas page (v136.3). Bundled to dist/storeRotas.js
// (globalName "StoreRotas"), loaded by admin.html ONLY.
//
// Matt approved 16 Oct 2026, from a Head Office user: "Can he access all the store rotas from the admin tab? He finds
// it annoying having to flick back into the staff app."
//
// READ ONLY. One store's week as the staff app's rota grid shows it: one row per person, one column per day. Nothing
// here writes a shift; "Edit in the staff app" opens index.html at that store's rota (?screen=rota&rotastore=NAME,
// honoured there only for a store the signed-in person may already pick, src/auth/deep-link.js).
//
// Data: GET /api/shifts?week=&store= through DB.shifts.list, the same read the staff app's rota makes (home shifts,
// people covering in, and the store's own people working away that week).
//
// Scope (who sees which stores) follows the other admin screens: Head Office (role 'admin', the isHeadOffice rule)
// every store; an area manager AREA_STORE_MAP[their area]; a store manager their own store. It is a courtesy on the
// screen: the read itself is the staff app's tokenless rota read, which any signed-in till can already make.
//
// The week: UAE weeks start on Monday, Qatar weeks on SUNDAY (shifts.week_start is anchored that way in the data:
// docs/field-lessons.md "The Monday assumption"). The start comes from dates.js (getMonday / getSunday, local time,
// never a UTC date string), exactly as the staff app's getWeekStart does for the store on screen.
//
// Globals read by bare name at call time (admin.html / dates.js / db.js): DB, currentAdmin, ALL_STORES, staffData,
// AREA_STORE_MAP, getStoreCountry, localDateStr, getMonday, getSunday, escapeHtml. Every one is guarded. (shiftMins is no
// longer read: the paid-hours rule uses the shared module's own copy, v136.7.)
//
// Colours are CSS variables only (docs/dark-mode.md), so the page follows Light and Dark with no extra work.
// Staff names and store names carry translate="no" (src/i18n): what people are called is never translated.

import { DAYS_UAE, DAYS_QATAR, buildModel } from '../shared/rota-cells.js';   // v136.7: the shared cell rules
import { coverAwareAreaStores } from '../shared/area-cover.js';   // v136.9: own area plus any area handed over for today

const LS_KEY = 'shift_admin_store_rotas_store';
export { DAYS_UAE, DAYS_QATAR };
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

let _store = null;    // the store on screen
let _offset = 0;      // weeks from this week (0 = this week), kept when the store changes
let _seq = 0;         // the newest load; an older answer that lands late is dropped

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function root() { return (typeof document !== 'undefined') ? document.getElementById('storeRotasRoot') : null; }

// ── Pure: which stores this person may look at ─────────────────────────────
// admin = currentAdmin; allStores = ALL_STORES (names); areaMap = AREA_STORE_MAP ({ area: [store names] }).
export function storesInScope(admin, allStores, areaMap) {
  if (!admin) return [];
  const role = String(admin.role || '').toLowerCase();
  const all = Array.isArray(allStores) ? allStores.filter((s) => typeof s === 'string' && s) : [];
  if (role === 'admin') return all.slice();
  if (role === 'area_manager') {
    const mine = coverAwareAreaStores(admin, areaMap);   // v136.9: + any area handed over for today
    return all.filter((s) => mine.indexOf(s) !== -1);
  }
  if (role === 'store_manager') return admin.store && all.indexOf(admin.store) !== -1 ? [admin.store] : [];
  return [];
}

// UAE first, then Qatar (the order of the other admin store pickers). countryOf(name) -> 'ELC - UAE' | 'ELC - Qatar'.
export function groupByCountry(stores, countryOf) {
  const uae = [], qatar = [];
  (stores || []).forEach((s) => { (countryOf(s) === 'ELC - Qatar' ? qatar : uae).push(s); });
  const out = [];
  if (uae.length) out.push({ label: 'UAE', stores: uae });
  if (qatar.length) out.push({ label: 'Qatar', stores: qatar });
  return out;
}

// ── Pure: the week ──────────────────────────────────────────────────────────
export function isQatarStore(store, countryOf) {
  return typeof countryOf === 'function' && countryOf(store || '') === 'ELC - Qatar';
}
export function daysFor(qatar) { return (qatar ? DAYS_QATAR : DAYS_UAE).slice(); }
// The first day of the week `offset` weeks from the week holding `now` (local midnight). Qatar on Sunday, UAE on
// Monday: dates.js's getSunday / getMonday, the helpers the staff rota's getWeekStart uses.
export function weekStartFor(qatar, offset, now) {
  const base = qatar ? getSunday(now || new Date()) : getMonday(now || new Date());
  base.setDate(base.getDate() + 7 * (parseInt(offset, 10) || 0));
  return base;
}
// 'Mon 12 Oct', the shape the Arabic catalogue already rebuilds word by word.
export function dayLabel(weekStart, i) {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + i);
  return DAY_NAMES[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()];
}
export function weekLabel(weekStart) {
  const d = new Date(weekStart);
  return 'Week commencing ' + DAY_NAMES[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
}

// ── Pure: the cells, as the staff app's rota draws them (src/rota/index.js renderRotaWeek) ──────────────────
// v136.7: the cell rules (shortStore, legHours, cellFor, buildModel) moved VERBATIM to src/shared/rota-cells.js so the
// Worker's Store overview and its email draw a week exactly as this page does. Re-exported here, so StoreRotas.buildModel
// and the rest keep their names and their behaviour.
export { shortStore, legHours, cellFor, buildModel } from '../shared/rota-cells.js';

// ── Rendering (variables only, docs/dark-mode.md) ───────────────────────────
const PILL = {
  work: 'background:var(--ok-bg);color:var(--ok-deep)',
  off: 'background:var(--danger-bg);color:var(--danger-deep)',
  toil: 'background:var(--surface-3);color:var(--text)',
  holiday: 'background:var(--warn-bg);color:var(--warn-deep)',
  sick: 'background:var(--danger-bg);color:var(--danger-deep)',
  apl: 'background:var(--surface-3);color:var(--text)',
  oos: 'background:var(--info-bg);color:var(--info-deep)',
  away: 'background:var(--info-bg);color:var(--info-deep)',
  empty: 'color:var(--sub)',
  none: 'color:var(--sub)',
};
const CSS = '#storeRotasRoot .srota-wrap{overflow-x:auto;background:var(--card);border:1px solid var(--border);border-radius:12px}' +
  '#storeRotasRoot table.srota{border-collapse:collapse;width:100%;font-size:12px;color:var(--text)}' +
  '#storeRotasRoot table.srota th{background:var(--surface-2);color:var(--sub);font-weight:700;padding:8px 6px;border-bottom:1px solid var(--border);white-space:nowrap}' +
  '#storeRotasRoot table.srota td{padding:6px;border-bottom:1px solid var(--border);text-align:center;vertical-align:middle}' +
  '#storeRotasRoot table.srota td.srota-name{text-align:start;white-space:nowrap;font-weight:600}' +
  '#storeRotasRoot table.srota tr.srota-cover td{background:var(--surface-2)}' +
  '#storeRotasRoot table.srota tr.srota-heads td{font-weight:700;background:var(--surface-2)}' +
  '#storeRotasRoot .srota-pill{display:inline-block;padding:3px 7px;border-radius:6px;font-size:11px;font-weight:700;white-space:nowrap}' +
  '#storeRotasRoot .srota-badge{display:inline-block;margin-inline-start:6px;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:700;background:var(--warn-bg);color:var(--warn-deep)}' +
  '#storeRotasRoot .srota-bar{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:12px}' +
  '#storeRotasRoot select.srota-store{padding:8px;border:1px solid var(--border);border-radius:8px;background:var(--card);color:var(--text);font:inherit;max-width:100%}';
function ensureCss() {
  if (typeof document === 'undefined' || document.getElementById('storeRotasCss')) return;
  const st = document.createElement('style');
  st.id = 'storeRotasCss';
  st.textContent = CSS;
  document.head.appendChild(st);
}

function cellHtml(c) {
  if (c.kind === 'empty' || c.kind === 'none') {
    return '<td' + (c.title ? ' title="' + esc(c.title) + '"' : '') + '><span style="' + PILL[c.kind] + '">' + esc(c.text) + '</span></td>';
  }
  let inner = '<span class="srota-pill" style="' + PILL[c.kind] + '"' + (c.title ? ' title="' + esc(c.title) + '"' : '') + '>' +
    (c.icon ? '<span aria-hidden="true">' + esc(c.icon) + '</span> ' : '') + esc(c.text) +
    (c.place ? '<span translate="no">' + esc(c.place) + '</span>' : '') + '</span>';
  if (c.second) {
    inner += '<div style="margin-top:3px"><span class="srota-pill" style="' + PILL.work + ';opacity:.85" title="Second shift on this day">' + esc(c.second) +
      (c.secondPlace ? ' ↗ <span translate="no">' + esc(c.secondPlace) + '</span>' : '') + '</span></div>';
  }
  return '<td>' + inner + '</td>';
}

// The grid for a model. weekStart (a Date) only labels the columns.
export function renderGrid(model, weekStart) {
  if (!model.people.length || model.empty) {
    return '<div class="ui-empty"><div class="ui-empty-title">No rota for this week yet.</div>' +
      '<div>Shifts are added in the staff app.</div></div>';
  }
  let html = '<div class="srota-wrap"><table class="srota"><thead><tr><th style="text-align:start">Staff</th>';
  model.days.forEach((d, i) => { html += '<th>' + esc(dayLabel(weekStart, i)) + '</th>'; });
  html += '<th>Hours</th></tr></thead><tbody>';
  model.people.forEach((p) => {
    html += '<tr' + (p.cover ? ' class="srota-cover"' : '') + '><td class="srota-name">' + (p.name ? '<span translate="no">' + esc(p.name) + '</span>' : '<span>Someone from another store</span>') +
      (p.cover ? '<span class="srota-badge">Cover</span>' + (p.home ? '<div style="font-size:10px;font-weight:400;color:var(--sub)" translate="no">' + esc(p.home) + '</div>' : '') : '') + '</td>';
    model.days.forEach((d) => { html += cellHtml(p.cells[d]); });
    html += '<td style="color:var(--sub);white-space:nowrap">' + (p.hours > 0 ? p.hours.toFixed(1) + 'h' : '-') + '</td></tr>';
  });
  html += '<tr class="srota-heads"><td class="srota-name">Heads</td>';
  model.days.forEach((d) => { html += '<td>' + (model.heads[d] || '-') + '</td>'; });
  html += '<td></td></tr></tbody></table></div>';
  return html;
}

// ── The link to the staff app ───────────────────────────────────────────────
// index.html at this store's rota. Keeps the page's own settings (a tenant hint), never a one-time link from here.
export function editUrl(store, search) {
  let sp;
  try { sp = new URLSearchParams(String(search == null ? '' : search)); } catch (e) { sp = new URLSearchParams(); }
  sp.delete('screen');
  sp.delete('rotastore');
  sp.set('screen', 'rota');
  if (store) sp.set('rotastore', store);
  return 'index.html?' + sp.toString();
}

// ── The page ────────────────────────────────────────────────────────────────
function countryOf(s) {
  try { return (typeof getStoreCountry === 'function') ? getStoreCountry(s) : (String(s).indexOf('Doha') === 0 ? 'ELC - Qatar' : 'ELC - UAE'); }
  catch (e) { return 'ELC - UAE'; }
}
function myStores() {
  return storesInScope(
    (typeof currentAdmin !== 'undefined') ? currentAdmin : null,
    (typeof ALL_STORES !== 'undefined') ? ALL_STORES : [],
    (typeof AREA_STORE_MAP !== 'undefined') ? AREA_STORE_MAP : {},
  );
}
function remembered() { try { return localStorage.getItem(LS_KEY); } catch (e) { return null; } }
function remember(s) { try { localStorage.setItem(LS_KEY, s); } catch (e) { /* private window: just not remembered */ } }

function skeleton() {
  const bar = (w) => '<span class="ui-skel" style="width:' + w + ';height:16px;margin:0 0 10px"></span>';
  return '<div class="card" aria-busy="true" aria-label="Loading the rota">' + bar('40%') + bar('100%') + bar('100%') + bar('92%') + bar('96%') + '</div>';
}

export function render() {
  const el = root();
  if (!el) return;
  ensureCss();
  const stores = myStores();
  const head = '<div style="margin-bottom:14px"><div style="font-size:18px;font-weight:800">Store rotas</div>' +
    '<div style="font-size:13px;color:var(--sub)">Read only: to change a shift, use Edit in the staff app.</div></div>';
  if (!stores.length) {
    el.innerHTML = head + '<div class="ui-empty"><div class="ui-empty-title">No stores in your scope.</div></div>';
    return;
  }
  if (!_store || stores.indexOf(_store) === -1) {
    const saved = remembered();
    _store = (saved && stores.indexOf(saved) !== -1) ? saved : stores[0];
  }
  const groups = groupByCountry(stores, countryOf);
  const opts = groups.map((g) => '<optgroup label="' + esc(g.label) + '">' + g.stores.map((s) =>
    '<option value="' + esc(s) + '"' + (s === _store ? ' selected' : '') + ' translate="no">' + esc(s) + '</option>').join('') + '</optgroup>').join('');
  el.innerHTML = head +
    '<div class="srota-bar">' +
      '<select class="srota-store" id="storeRotasStore" aria-label="Store" onchange="StoreRotas.pickStore(this.value)">' + opts + '</select>' +
      '<button type="button" class="btn btn-outline" onclick="StoreRotas.go(-1)">&lsaquo; Previous</button>' +
      '<button type="button" class="btn btn-outline" onclick="StoreRotas.go(0)">This week</button>' +
      '<button type="button" class="btn btn-outline" onclick="StoreRotas.go(1)">Next &rsaquo;</button>' +
      '<button type="button" class="btn btn-primary" onclick="StoreRotas.editInStaffApp()">Edit in the staff app</button>' +
    '</div>' +
    '<div id="storeRotasWeek" style="font-size:13px;font-weight:700;margin-bottom:10px"></div>' +
    '<div id="storeRotasGrid"></div>';
  load();
}

export async function load() {
  const grid = (typeof document !== 'undefined') ? document.getElementById('storeRotasGrid') : null;
  const wkEl = (typeof document !== 'undefined') ? document.getElementById('storeRotasWeek') : null;
  if (!grid || !_store) return;
  const my = ++_seq;
  const store = _store;
  const qatar = isQatarStore(store, countryOf);
  const ws = weekStartFor(qatar, _offset, new Date());
  const weekStr = localDateStr(ws);
  const days = daysFor(qatar);
  if (wkEl) wkEl.textContent = weekLabel(ws);
  grid.innerHTML = skeleton();
  let rows;
  try {
    if (typeof DB === 'undefined' || !DB.shifts || typeof DB.shifts.list !== 'function') throw new Error('The app has not finished loading. Refresh the page and try again.');
    rows = await DB.shifts.list({ week: weekStr, store });
    if (!Array.isArray(rows)) throw new Error('The server did not send a rota. Try again in a moment.');
  } catch (e) {
    if (my !== _seq) return;
    // Says what went wrong: the reason is the error's own message (the server's, or one of the two above).
    grid.innerHTML = '<div class="card" role="alert" style="background:var(--danger-bg);color:var(--danger-deep);font-size:13px">' +
      '<div style="font-weight:700">Could not load the rota.</div><div>' + esc((e && e.message) || 'Unknown error') + '</div></div>';
    return;
  }
  if (my !== _seq) return;   // the store or week changed while this was loading
  const model = buildModel({ store, rows, staff: (typeof staffData !== 'undefined') ? staffData : [], weekStr, days });
  grid.innerHTML = renderGrid(model, ws);
}

export function pickStore(name) {
  if (myStores().indexOf(name) === -1) return;   // only a store this person may see
  _store = name;
  remember(name);
  load();
}
// dir: -1 previous, 1 next, 0 this week.
export function go(dir) {
  const n = parseInt(dir, 10) || 0;
  _offset = n === 0 ? 0 : _offset + n;
  load();
}
export function editInStaffApp() {
  if (!_store) return;
  location.href = editUrl(_store, location.search);
}
